import React, { FC }             from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Grid,
  Typography,
  IconButton,
  Collapse,
  Container,
  TableCellProps,
  Box,
}                                from '@material-ui/core';
import {
  makeStyles,
  createStyles,
}                                from '@material-ui/core/styles';
import {
  ExpandLessRounded,
  ExpandMoreRounded,
}                                from '@material-ui/icons';
import {
  TDataTableRow,
  IDataTableHeader,
}                                from '@typings/components';
import { LinkButton }            from '@components/index';
import { StatisticsTableButton } from './StatisticsTableButton';

const useStyles = makeStyles(({ palette, spacing }) => createStyles({
  content: {
    paddingBlock : `${spacing(2)}px`,
    textAlign    : 'center',
  },
  tableContainer  : { padding: `${spacing(2)}px` },
  firstHeaderCell : { border: 'none' },
  firstTableCell  : {
    borderBottom : `1px solid ${palette.grey[300]}`,
    fontSize     : 16,
  },
  tableCell: {
    borderBottom : `1px solid ${palette.grey[300]}`,
    borderLeft   : `1px solid ${palette.grey[300]}`,
    fontSize     : 16,
  },
  titleBtnPlaceholder : { paddingLeft: `${spacing(6)}px` },
  tableNote           : { fontStyle: 'italic' },
  linkWrapper         : {
    textAlign    : 'right',
    marginBottom : `${spacing(2)}px`,
  },
}));

const getCellClasses = (
  index            : number,
  firstCellClasses : string,
  cellClasses      : string,
): TableCellProps['classes'] => ({
  root: index === 0 ? firstCellClasses : cellClasses,
});

interface IProps {
  tableTitle             : string;
  headers                : IDataTableHeader[];
  subHeaders             : IDataTableHeader[];
  tableNoteText?         : string;
  rows                   : TDataTableRow[];
  withPeriodCell?        : boolean;
  isTableExpanded?       : boolean;
  toggleIsTableExpanded? : () => void;
  downloadExcel          : () => Promise<void>;
  onLinkClick?           : () => void;
}

export const StatisticsTable: FC<IProps> = ({
  tableTitle,
  headers,
  subHeaders,
  tableNoteText,
  rows,
  withPeriodCell  = false,
  isTableExpanded = true,
  toggleIsTableExpanded,
  downloadExcel,
  onLinkClick,
}) => {
  const {
    content,
    tableContainer,
    firstHeaderCell,
    firstTableCell,
    tableCell,
    titleBtnPlaceholder,
    tableNote,
    linkWrapper,
  } = useStyles();

  return (
    <Collapse
      in              = {isTableExpanded}
      collapsedHeight = {75}
    >
      <Container
        maxWidth="xl"
        disableGutters
      >
        <Paper classes={{ root: content }}>
          <Grid
            justify    = "space-between"
            alignItems = "center"
            container
          >
            <Typography
              variant = "h6"
              classes = {{ root: !toggleIsTableExpanded ? titleBtnPlaceholder : undefined }}
            >
              {toggleIsTableExpanded && (
                <IconButton
                  color   = "secondary"
                  onClick = {toggleIsTableExpanded}
                >
                  {isTableExpanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                </IconButton>
              )}

              <span>{tableTitle}</span>
            </Typography>

            <StatisticsTableButton onClick={downloadExcel} />
          </Grid>

          <Container
            classes  = {{ root: tableContainer }}
            maxWidth = "xl"
          >
            {onLinkClick && (
              <Box className={linkWrapper}>
                <LinkButton
                  size    = "medium"
                  label   = "See VIN Decode History"
                  onClick = {onLinkClick}
                />
              </Box>
            )}

            {tableNoteText && (
              <Typography
                align   = "right"
                classes = {{ root: tableNote }}
                paragraph
              >
                {tableNoteText}
              </Typography>
            )}

            <Table>
              <TableHead>
                <TableRow>
                  {headers.map(({ label, align, colSpan }, index) => (
                    <TableCell
                      key     = {label}
                      align   = {align}
                      colSpan = {colSpan}
                      classes = {getCellClasses(
                        index,
                        withPeriodCell ? firstHeaderCell : firstTableCell,
                        tableCell,
                      )}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {subHeaders.map(({ label, align }, index) => (
                    <TableCell
                      key     = {`${label}_${index}`}
                      align   = {align}
                      classes = {getCellClasses(index, firstTableCell, tableCell)}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    {row.map((
                      { value, align },
                      cellIndex,
                    ) => (
                      <TableCell
                        key     = {cellIndex}
                        align   = {align}
                        classes = {getCellClasses(cellIndex, firstTableCell, tableCell)}
                      >
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Container>
        </Paper>
      </Container>
    </Collapse>
  );
};
